// Horizontal "Filtered Statistics" strip — stage totals always visible,
// expand to see the per-AM breakdown. Sits directly under the filter bar.
import { useMemo, useState } from "react";
import { BarChart3, ChevronDown, ChevronRight } from "lucide-react";

const GBP = (n) => "£" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

function StageChip({ r, testid }) {
  return (
    <span
      data-testid={testid}
      title={`${r.stage_label}: ${r.count} · ${GBP(r.ltv)} LTV${r.open_value ? ` · ${GBP(r.open_value)} open` : ""}`}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/10 text-[11px] whitespace-nowrap"
    >
      <span className="text-slate-300">{r.stage_label}</span>
      <span className="text-white font-semibold tabular-nums">{r.count}</span>
      <span className="text-emerald-300 tabular-nums">{GBP(r.ltv)}</span>
      {r.open_value > 0 && <span className="text-orange-300 tabular-nums">({GBP(r.open_value)} open)</span>}
    </span>
  );
}

export default function DealStatsStrip({ stats, stages = [], periodLabel }) {
  const [open, setOpen] = useState(false);
  const rows = useMemo(() => stats?.by_am_stage || [], [stats]);
  const order = useMemo(() => Object.fromEntries(stages.map((s, i) => [s.key, i])), [stages]);

  const byStage = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const a = (m[r.stage_key] ||= { stage_key: r.stage_key, stage_label: r.stage_label, count: 0, ltv: 0, open_value: 0 });
      a.count += r.count; a.ltv += r.ltv; a.open_value += r.open_value || 0;
    }
    return Object.values(m).sort((a, b) => (order[a.stage_key] ?? 999) - (order[b.stage_key] ?? 999));
  }, [rows, order]);

  const byAm = useMemo(() => {
    const m = {};
    for (const r of rows) (m[r.am] ||= []).push(r);
    return Object.entries(m).sort(([a], [b]) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [rows]);

  if (!stats) return null;
  const totals = stats.totals || {};

  return (
    <div data-testid="deal-stats-strip" className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.04] px-3 py-2">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="deal-stats-toggle"
          disabled={byAm.length === 0}
          className="shrink-0 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-emerald-300 hover:text-emerald-100 disabled:opacity-60"
          title={open ? "Hide per-AM breakdown" : "Show per-AM breakdown"}
        >
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <BarChart3 className="w-3 h-3" /> Filtered stats
          {periodLabel && <span className="text-emerald-500/60 normal-case tracking-normal">· {periodLabel}</span>}
        </button>
        <span className="shrink-0 text-[11px] font-mono tabular-nums text-slate-300" data-testid="deal-stats-totals">
          <span className="text-white font-semibold">{totals.count ?? 0}</span> · <span className="text-emerald-300 font-semibold">{GBP(totals.ltv)}</span>
          {totals.open_value > 0 && <> · <span className="text-orange-300">{GBP(totals.open_value)} open</span></>}
        </span>
        <span className="w-px h-4 bg-white/10 shrink-0" />
        <div className="flex items-center gap-1.5 flex-wrap min-w-0" data-testid="deal-stats-stages">
          {byStage.length === 0
            ? <span className="text-[11px] text-slate-500 font-mono">No matching records.</span>
            : byStage.map((r) => <StageChip key={r.stage_key} r={r} testid={`deal-stats-stage-${r.stage_key}`} />)}
        </div>
      </div>
      {open && byAm.length > 0 && (
        <div className="mt-2 pt-2 border-t border-white/[0.06] max-h-[220px] overflow-y-auto space-y-1" data-testid="deal-stats-breakdown">
          {byAm.map(([am, list]) => (
            <div key={am} className="flex items-center gap-2 flex-wrap" data-testid={`deal-stats-am-${am}`}>
              <span className="w-36 shrink-0 text-[12px] font-semibold text-white truncate">{am}</span>
              {list.map((r) => <StageChip key={r.stage_key} r={r} testid={`deal-stats-chip-${am}-${r.stage_key}`} />)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
